import { useContext, useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { DISTRIBUTION_ERRORS } from 'shared/constants/GenericMessages';
import { FundsDistributionList } from '../FundsDistributionList/FundsDistributionList';
import { formatCurrency } from 'shared/utils';
import { Col, Row } from 'react-bootstrap';
import { FormWithValidation } from 'shared/form';
import { FundsDistributionContext } from 'core/cuenta/contexts/FundsDistributionContext';
import { BaseModal, useModal } from 'shared/modal';
import { DistribucionDeFondosRequest } from 'core/cuenta/models/requests/DistribucionDeFondosRequest';
import { FundsDistributionOrigin } from '../FundsDistributionOrigin/FundsDistributionOrigin';
import { Button, Modal } from '@orbita-ui/core';

interface FormValues {
  [key: string]: number;
}

const getTotalValue = (values: FormValues) =>
  Object.values(values).reduce((sum: number, val: number) => sum + (val || 0), 0);

/**
 * Logica que se usa para validar el formulario.
 * Referencia:
 * https://react-hook-form.com/api/useform/#resolver
 *
 * El objeto de error que se construye acá es el objeto que usa el formulario para validar los campos
 * solo DESPUES de llamar a la funcion trigger() del hook (que actualiza el estado de error del formulario completo)
 * https://react-hook-form.com/api/useform/trigger/
 *
 * @param values valores actuales del formulario
 * @param context propiedad context del useForm
 * @param obj contiene proiedades del formulario, en este caso se usan los nombres de los inputs registrados
 */
const formResolver: Resolver<FormValues> = async (values, { disponible }, { names }) => {
  const sum = getTotalValue(values);
  const errors = {};
  if (sum > disponible) {
    errors['message'] = DISTRIBUTION_ERRORS.amountExceedsMax;
  } else if (sum === 0) {
    errors['message'] = DISTRIBUTION_ERRORS.finalAmountIsZero;
  }
  if (errors['message']) {
    for (const key of names) {
      errors[key] = {
        type: 'validate',
        message: '',
      };
    }
  }

  return {
    values,
    errors,
  };
};

export const FundsDistributionForm = () => {
  const { origen, distributionErrorMessage, destinos, isLoading, sendDistribution } =
    useContext(FundsDistributionContext);
  const methods = useForm({
    resolver: formResolver,
    defaultValues: {},
    context: {
      disponible: origen?.saldoDisponible || 0,
    },
  });
  const {
    formState: { errors, isValid, isSubmitted },
    getValues,
    trigger,
    reset,
  } = methods;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showModal, closeModal, openModal } = useModal(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // al cambiar el estado del formulario, actualizamos el estado de error (valido o invalido) de los inputs
    if (isSubmitted) {
      trigger();
      openModal();
    }
  }, [trigger, isSubmitted, openModal]);

  useEffect(() => {
    if (!isLoading && !distributionErrorMessage) {
      const values = getValues();
      Object.keys(values).forEach((k) => (values[k] = '' as any));
      reset(values);
    }
  }, [isLoading, distributionErrorMessage, reset, getValues]);

  useEffect(() => {
    if (errorMessage) {
      openModal();
    }
  }, [errorMessage, openModal]);

  useEffect(() => {
    const values = getValues();
    let localError = null;
    if (!isValid) {
      localError = errors?.message || null;
    } else if (distributionErrorMessage) {
      localError = distributionErrorMessage;
    } else if (Object.values(values).some((value) => value < 0)) {
      localError = DISTRIBUTION_ERRORS.amountBelowMin;
    }
    setErrorMessage(localError);
  }, [isValid, errors, distributionErrorMessage, getValues]);

  const doSubmit = () => {
    closeModal();
    const transferencias = [];
    for (const val in getValues()) {
      const numeroCuenta = val.replace(/distribution/, '');
      transferencias.push({
        numeroCuenta,
        monto: getValues()[val],
      });
    }
    sendDistribution(new DistribucionDeFondosRequest(null, transferencias));
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <>
      <Modal show={showModal} onClose={closeModal}>
        {errorMessage ? (
          <BaseModal
            state="error"
            title="Error"
            body={<p>{errorMessage}</p>}
            footer={
              <Button color="destructive" onClick={closeModal}>
                Cerrar
              </Button>
            }
          />
        ) : (
          <BaseModal
            title="Realizar transferencia"
            body={
              <p>
                Va a transferir un total de {formatCurrency(getTotalValue(getValues()))}.
                ¿Está seguro?
              </p>
            }
            footer={
              <>
                <Button color="destructive" variant="outline" onClick={closeModal}>
                  Cancelar
                </Button>
                <Button onClick={doSubmit}>Aceptar</Button>
              </>
            }
          />
        )}
      </Modal>
      <FormWithValidation reactFormHookMethods={methods} onSubmit={openModal}>
        <Row className="mb-4">
          <Col>
            <FundsDistributionOrigin />
          </Col>
        </Row>
        <FundsDistributionList
          key={refreshKey}
          isLoading={isLoading}
          onSubmit={openModal}
          saldoDisponible={origen?.saldoDisponible}
          destinos={destinos}
          onClear={() => reset()}
        />
      </FormWithValidation>
    </>
  );
};
